import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MdCheck } from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import Button from "components/button";
import SideDrawer from "components/drawer/SideDrawer";
import notify from "lib/notify";
import AssignForm from "./assign-form";
import ModernServiceForm from "./components/ModernServiceForm";

const FORM_ID = "service-form";

const STEPS = [
  { id: "details", label: "Setup Service" },
  { id: "assign", label: "Assign People" },
];

const StepIndicator = ({ current }) => {
  const currentIndex = STEPS.findIndex((step) => step.id === current);
  return (
    <ol className="mb-6 flex select-none gap-1 rounded-control bg-surface-subtle p-1">
      {STEPS.map((step, index) => {
        const active = index === currentIndex;
        const done = index < currentIndex;
        return (
          <li
            key={step.id}
            aria-current={active ? "step" : undefined}
            className={`flex flex-1 items-center gap-2 rounded-control px-3 py-2 text-sm font-semibold ${
              active ? "bg-surface text-fg shadow-sm" : "text-fg-muted"
            }`}
          >
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${
                active || done ? "bg-primary text-primary-fg" : "bg-surface text-fg-muted"
              }`}
            >
              {done ? <MdCheck aria-hidden="true" className="h-4 w-4" /> : index + 1}
            </span>
            {step.label}
          </li>
        );
      })}
    </ol>
  );
};

// Safe date formatting for API (no timezone shift): returns MM/DD/YYYY
function formatDateForApi(date) {
  if (!date) return null;

  if (typeof date === "string" && date.includes("/")) {
    return date;
  }

  if (typeof date === "string" && date.includes("-")) {
    const [year, month, day] = date.split("-");
    return `${month}/${day}/${year}`;
  }

  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${month}/${day}/${year}`;
  }

  return date;
}

function buildPayload(service, extra = {}) {
  if (service?.fileObjects && service.fileObjects.length > 0) {
    const formData = new FormData();

    Object.keys(service).forEach((key) => {
      if (key === "fileObjects") return;
      if (key === "date") {
        formData.append(key, service?.date ? formatDateForApi(service?.date) : null);
      } else if (key === "location") {
        formData.append(key, JSON.stringify(service[key]));
      } else {
        formData.append(key, service[key]);
      }
    });

    Object.entries(extra).forEach(([key, value]) => formData.append(key, String(value)));

    service.fileObjects.forEach((file) => {
      formData.append("serviceFiles", file);
    });

    return { payload: formData, headers: { "Content-Type": "multipart/form-data" } };
  }

  return {
    payload: {
      ...service,
      ...extra,
      date: service?.date ? formatDateForApi(service?.date) : null,
    },
    headers: {},
  };
}

const ServiceForm = (props) => {
  const [queryParams] = useSearchParams();
  const { isOpen, onClose, service, setService, selected, setSelected, getServices } = props;

  const [tab, setTab] = useState(queryParams.get("tab") || "details");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!service.name?.trim()) newErrors.name = "Service name is required";
    if (!String(service.type)?.trim()) newErrors.type = "Service type is required";
    if (!service.date?.trim()) newErrors.date = "Date is required";
    if (!service.startAt?.trim()) newErrors.startAt = "Start time is required";
    if (!service.endAt?.trim()) newErrors.endAt = "End time is required";
    if (!service.schedule?.trim()) newErrors.schedule = "Repeat schedule is required";

    if (service.startAt && service.endAt) {
      const parseTime = (timeStr) => {
        const [time, modifier] = timeStr.split(" ");
        let [hours, minutes] = time.split(":").map(Number);

        if (modifier === "PM" && hours !== 12) hours += 12;
        if (modifier === "AM" && hours === 12) hours = 0;

        return hours * 60 + minutes;
      };

      const start = parseTime(service.startAt);
      const end = parseTime(service.endAt);

      if (start >= end) {
        newErrors.startAt = "Start time must be earlier than end time";
      }

      // Parse the MM/DD/YYYY date in the local timezone
      const dateParts = service.date.split("/");
      const selectedDate = new Date(dateParts[2], dateParts[0] - 1, dateParts[1]);
      const now = new Date();

      const isToday =
        selectedDate.getFullYear() === now.getFullYear() &&
        selectedDate.getMonth() === now.getMonth() &&
        selectedDate.getDate() === now.getDate();

      if (isToday) {
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        if (start <= currentMinutes) {
          newErrors.startAt = "Start time must be in the future";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSaveChanges = async (e, skipAssign) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (tab !== "details") return;

    setLoading(true);
    if (service?.id) {
      try {
        const { payload, headers } = buildPayload(service, { isEdit: true });
        const response = await AtriumApi.put(`/v1/services/${service?.id}`, payload, { headers });

        if (response?.status === 200) {
          await getServices();
          notify.success("Service saved");
          skipAssign ? onClose() : setTab("assign");
        }
      } catch (error) {
        notify.error(getApiErrorMessage(error, "Could not save service"));
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const { payload, headers } = buildPayload(service);
        const response = await AtriumApi.post("/v1/services", payload, { headers });
        if (response) {
          setService(response?.data?.service);
          await getServices();
          notify.success("Service created");
          skipAssign ? onClose() : setTab("assign");
        }
      } catch (error) {
        notify.error(getApiErrorMessage(error, "Could not create service"));
      } finally {
        setLoading(false);
      }
    }
  };

  const assignPeople = async (e) => {
    e.preventDefault();
    if (tab !== "assign") return;

    try {
      setLoading(true);
      const response = await AtriumApi.put(`/v1/services/${service?.id}/people`, {
        users: selected?.map((u) => u?.id),
      });
      if (response) {
        notify.success("Assignments saved");
        onClose();
        getServices();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not assign people"));
    } finally {
      setLoading(false);
    }
  };

  const footer =
    tab === "details" ? (
      <>
        {service?.id ? (
          <Button variant="secondary" onClick={() => onClose()} disabled={loading}>
            Cancel
          </Button>
        ) : (
          <Button variant="secondary" onClick={(e) => onSaveChanges(e, true)} disabled={loading}>
            Skip Assigning
          </Button>
        )}
        <Button type="submit" form={FORM_ID} loading={loading}>
          {service?.id ? "Save and Assign" : "Create and Assign"}
        </Button>
      </>
    ) : (
      <>
        {service?.id ? (
          <Button variant="secondary" onClick={() => onClose()} disabled={loading}>
            Cancel
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => setTab("details")} disabled={loading}>
            Back
          </Button>
        )}
        <Button onClick={assignPeople} loading={loading}>
          {service?.id ? "Save Changes" : "Create Service"}
        </Button>
      </>
    );

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={service?.id ? "Edit Service" : "Create Service"}
      size="lg"
      footer={footer}
    >
      <StepIndicator current={tab} />

      {tab === "details" && (
        <form id={FORM_ID} onSubmit={onSaveChanges} noValidate>
          <ModernServiceForm
            service={service}
            setService={setService}
            errors={errors}
            setErrors={setErrors}
          />
        </form>
      )}

      {tab === "assign" && <AssignForm selected={selected} setSelected={setSelected} />}
    </SideDrawer>
  );
};

export default ServiceForm;
